// ═══════════════════════════════════════════════════════════════════
//  TEAM MEMBERSHIP
//
//  Who from CMS is on a Cal team, and what has to be sent to change it.
//
//  Three lists have to be joined to answer that: CMS's own users
//  (GET /api/users), the team's roster (GET /api/ams/teams/{id}/members),
//  which names Cal ids and nothing else, and the Cal organization's users
//  (GET /api/ams/cal-users), which carry the email the two sides are
//  matched on. The server matches the same way — by email, case-
//  insensitively — see CalTeamAssignmentService.
// ═══════════════════════════════════════════════════════════════════

import type {
  CalOrganizationUser,
  CalTeamMember,
  CmsUser,
  TeamAssignmentReport,
} from "../bookingCalendarApi"

/** Most principal names one assignment request may carry — TeamAssignmentRequest caps it at 200. */
export const MAX_UPNS_PER_REQUEST = 200

export interface MembershipRow {
  user: CmsUser
  /** Whether they are on the team right now. */
  isMember: boolean
  /** Their Cal account, when they have one. Absent for staff Cal has never been told about. */
  calUserId?: number
  /** False for a member who has not accepted the invitation — they are on the team but host nothing. */
  accepted: boolean
}

export interface MembershipRoster {
  rows: MembershipRow[]
  /**
   * Members no CMS user accounts for — a Cal account whose address matches no CMS user. They cannot
   * be managed from here (the endpoint addresses people by principal name), so they are reported
   * rather than silently missing from a list that otherwise reads as the whole roster.
   *
   * Only members the organization's user list names are reported. The server leaves the Cal account
   * CMS itself acts as out of every roster and every user list; a member it cannot name is dropped
   * rather than shown as "Staff #id", so that account never surfaces here even if a roster ever
   * carried it.
   */
  unmatched: { calUserId: number; label: string }[]
}

/**
 * The roster as a checklist: one row per CMS user, ticked when they are on the team.
 *
 * Rows come back in the order `cmsUsers` arrived in, which is display-name order from the server.
 */
export function membershipRoster(
  cmsUsers: readonly CmsUser[],
  members: readonly CalTeamMember[],
  calUsers: readonly CalOrganizationUser[],
): MembershipRoster {
  const calUsersByEmail = new Map(calUsers.map(user => [user.email.trim().toLowerCase(), user]))
  const membersByCalUserId = new Map(members.map(member => [member.userId, member]))

  const claimed = new Set<number>()

  const rows = cmsUsers.map(user => {
    const calUser = calUsersByEmail.get(user.userPrincipalName.trim().toLowerCase())
    const member = calUser ? membersByCalUserId.get(calUser.id) : undefined

    if (member) claimed.add(member.userId)

    return {
      user,
      isMember: !!member,
      calUserId: calUser?.id,
      accepted: member?.accepted ?? false,
    }
  })

  const calUsersById = new Map(calUsers.map(user => [user.id, user]))

  const unmatched = members
    .filter(member => !claimed.has(member.userId))
    .flatMap(member => {
      const calUser = calUsersById.get(member.userId)
      if (!calUser) return []
      const label = calUser.name?.trim() || calUser.email.trim() || `Staff #${member.userId}`
      return [{ calUserId: member.userId, label }]
    })

  return { rows, unmatched }
}

/**
 * What has to change to make the team match `checked`, as the two calls that do it.
 *
 * Only rows that actually moved appear. A deactivated user cannot turn up in `add`: the dialog does
 * not offer one who is not already a member, and one who is starts checked — so unchecking them
 * removes them and re-checking them is a return to where they started, not an addition.
 */
export function membershipDiff(
  rows: readonly MembershipRow[],
  checked: ReadonlySet<string>,
): { add: string[]; remove: string[] } {
  const add: string[] = []
  const remove: string[] = []

  for (const row of rows) {
    const wanted = checked.has(row.user.userPrincipalName)

    if (wanted && !row.isMember) add.push(row.user.userPrincipalName)
    if (!wanted && row.isMember) remove.push(row.user.userPrincipalName)
  }

  return { add, remove }
}

/** Splits `items` into runs of at most `size`. Empty in, empty out — never a batch of nothing. */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  const batches: T[][] = []

  for (let index = 0; index < items.length; index += size) {
    batches.push(items.slice(index, index + size))
  }

  return batches
}

export interface MembershipChangeSummary {
  /** People now on the team who were asked to be added. */
  added: number
  /** People now off it who were asked to be removed. */
  removed: number
  /** The pairs Cal refused, which is the part of a bulk write worth showing. */
  failures: { upn: string; error: string }[]
  /**
   * The people this edit actually put on the team (status Added, no error), with the Cal id the
   * availability editor addresses them by. A fresh add starts them with no hours (AB#749), so these
   * are the people somebody now has to give a schedule. Re-ticking someone already on the team
   * (AlreadyMember) is not listed: their hours were left alone.
   */
  newlyAdded: AddedMember[]
}

/** One person a membership edit put on the team. */
export interface AddedMember {
  upn: string
  calUserId: number
  status: number | string
}

/**
 * TeamAssignmentStatus.Added. The API serializes the enum as its ordinal (Added = 0); the name is
 * accepted as well so a switch to string enums does not silently stop the hand-off to the editor.
 */
function isFreshAdd(status: number | string): boolean {
  return status === 0 || (typeof status === "string" && status.toLowerCase() === "added")
}

/** Where a host's hours at a site are set: the availability editor's host view (AB#781). */
export function availabilityEditorPath(teamSlug: string, calUserId: number): string {
  const params = new URLSearchParams({ site: teamSlug, view: "host", host: String(calUserId) })
  return `/appointments/availability?${params.toString()}`
}

/**
 * What the membership dialog does once a save has come back.
 *
 * - `editHours`: exactly one person was added and nothing failed — take the admin straight to that
 *   person's hours, since they are unbookable until somebody sets them.
 * - `setHours`: stay open, listing a "Set hours" link per person added (several, or alongside
 *   failures that still need reading). The list may be empty when only failures are left to show.
 * - `close`: nothing more to do (removals only, or re-ticked members).
 */
export type AfterMembershipSave =
  | { kind: "close" }
  | { kind: "editHours"; path: string }
  | { kind: "setHours"; links: { upn: string; path: string }[] }

export function afterMembershipSave(teamSlug: string, summary: MembershipChangeSummary): AfterMembershipSave {
  const links = summary.newlyAdded.map(member => ({
    upn: member.upn,
    path: availabilityEditorPath(teamSlug, member.calUserId),
  }))

  const [only] = links
  if (summary.failures.length === 0 && links.length === 1 && only) {
    return { kind: "editHours", path: only.path }
  }

  if (summary.failures.length > 0 || links.length > 1) {
    return { kind: "setHours", links }
  }

  return { kind: "close" }
}

/**
 * Folds the per-pair outcomes of one or more reports into what the dialog says afterwards.
 *
 * A pair is counted as applied when it carries no error, which folds in the two "already in that
 * state" outcomes — someone a second admin put on the team a moment ago is in the state the user
 * asked for, and saying otherwise would be a distinction without a difference here.
 *
 * `error` is what marks a failure, rather than the status enum: the API serializes that as its
 * ordinal, and a number read here would silently mean something else the day a case is inserted.
 */
export function summarizeAssignments(
  added: readonly TeamAssignmentReport[],
  removed: readonly TeamAssignmentReport[],
): MembershipChangeSummary {
  const failures: { upn: string; error: string }[] = []
  const newlyAdded: AddedMember[] = []

  function applied(reports: readonly TeamAssignmentReport[], collectAdded: boolean) {
    let total = 0

    for (const report of reports) {
      for (const result of report.results) {
        if (result.error) {
          failures.push({ upn: result.upn, error: result.error })
        } else {
          total++

          if (collectAdded && isFreshAdd(result.status) && typeof result.calUserId === "number") {
            newlyAdded.push({ upn: result.upn, calUserId: result.calUserId, status: result.status })
          }
        }
      }
    }

    return total
  }

  return {
    added: applied(added, true),
    removed: applied(removed, false),
    failures,
    newlyAdded,
  }
}
